import {
  IonButton,
  IonContent,
  IonLoading,
  IonPage,
  NavContext,
  useIonActionSheet
} from '@ionic/react';
import { useCallback, useContext, useEffect, useMemo } from 'react';

import { Capacitor } from '@capacitor/core';

import {
  ButtonContainer,
  Logo,
  Menu,
  Padding,
  Spacer
} from '@/components/common';
import { Skeleton } from '@/components/memo';
import {
  AddDentistTile,
  DentistTile,
  DentistTilePlaceholder
} from '@/components/profiles/selectDentist';
import {
  useAccount,
  useApp,
  useAuth,
  useClinic,
  useDentist,
  useMobileNumber,
  useSubscription
} from '@/contexts';
import { Dentist } from '@/data/Dentists';
import { withSelectDentistModals } from '@/hocs';
import { usePaywall } from '@/hooks';
import { Paywall } from '@/pages';
import { PLATFORMS, SAVE_ACCOUNT_DELAY_IN_MS } from '@/utils/constants';
import { formatDentistName } from '@/utils/helpers/formatDentistName';
import { getUserMobileNumber } from '@/utils/helpers/getUserMobileNumber';
import { ROUTES } from '@/utils/routes';
import { PROFILE_COUNT_LIMITS } from '@/utils/subscriptions';
import { PAYWALL_SOURCES } from '@/utils/tracker';
import { MenuItem } from '@/utils/types';
import { useQueryClient } from '@tanstack/react-query';
import {
  calendarNumberOutline,
  ellipsisVertical,
  logOutOutline,
  swapHorizontalOutline
} from 'ionicons/icons';

import './SelectDentist.css';

interface SelectDentistProps {
  modals: {
    overview: { isOpen: boolean; onOpen: () => void; onClose: () => void };
    addDentist: { isOpen: boolean; onOpen: () => void; onClose: () => void };
    addAccount: { isOpen: boolean; onOpen: () => void; onClose: () => void };
  };
}

const SelectDentist: React.FC<SelectDentistProps> = ({ modals }) => {
  const { navigate } = useContext(NavContext);
  const {
    shouldSaveAccount,
    setShouldSaveAccount,
    storedAccounts,
    switchToAccount,
    saveAccount,
    loading: loadingAccount
  } = useAccount();
  const { signOut, user } = useAuth();
  const { isPaused, isResumed } = useApp();
  const {
    selectedClinic: clinic,
    isClinic,
    fetchClinic,
    loading: loadingClinic,
    loaded: loadedClinic
  } = useClinic();
  const {
    dentists,
    selectDentist,
    fetchDentists,
    loading: loadingDentists,
    loaded: loadedDentists,
    reloading: reloadingDentists
  } = useDentist();
  const { saveMobileNumber } = useMobileNumber();
  const {
    profileCountLimit,
    isSubscribed,
    purchasing
  } = useSubscription();
  const { showPaywall, onOpenPaywall, onClosePaywall } = usePaywall();

  const copy = {
    prompt: isClinic ? 'Choose a dentist' : 'Choose a clinic',
    addLabel: 'Add Dentist',
    signOutButtonText: 'Sign Out',
    menu: {
      overview: 'Overview',
      switchAccount: 'Switch Account',
      signOut: 'Sign Out'
    },
    switchAccont: {
      header: 'Switch Account',
      subHeader: 'Choose an account below',
      addAccount: 'Add an account',
      signOutAllAccounts: 'Sign out all accounts',
      cancel: 'Cancel'
    }
  };

  const queryClient = useQueryClient();
  const [present] = useIonActionSheet();

  const isNativePlatform = Capacitor.isNativePlatform();

  const onSelectDentist = useCallback(
    async (dentist: Dentist) => {
      await selectDentist(dentist);
      navigate(ROUTES.CALENDAR);
    },
    [navigate, selectDentist]
  );

  const onAddDentist = useCallback(() => {
    if (dentists.length > 0 && isSubscribed === false) {
      onOpenPaywall({
        source: PAYWALL_SOURCES.PROFILE
      });
      return;
    }

    modals.addDentist.onOpen();
  }, [isSubscribed, onOpenPaywall, dentists.length, modals.addDentist.onOpen]);

  const onSignOut = useCallback(() => {
    signOut();
  }, [signOut]);

  const onSwitchAccount = useCallback(() => {
    const accountOptions = storedAccounts.map((account) => ({
      text: account.displayName,
      handler: () => {
        switchToAccount(account);
      }
    }));

    present({
      header: copy.switchAccont.header,
      subHeader: copy.switchAccont.subHeader,
      buttons: [
        ...accountOptions,
        {
          text: copy.switchAccont.addAccount,
          cssClass: 'add-account',
          handler: async () => {
            modals.addAccount.onOpen();
          }
        },
        {
          text: copy.switchAccont.signOutAllAccounts,
          role: 'destructive',
          handler: async () => {
            signOut(true, true);
          }
        },
        {
          text: copy.switchAccont.cancel,
          role: 'cancel'
        }
      ]
    });
  }, [
    storedAccounts,
    switchToAccount,
    present,
    signOut,
    modals.addAccount.onOpen
  ]);

  const fetchProfiles = useCallback(async () => {
    if (!loadingClinic && !loadedClinic) {
      fetchClinic();
    }
    if (isClinic !== undefined && !reloadingDentists) {
      fetchDentists();
    }
  }, [
    fetchClinic,
    fetchDentists,
    loadedClinic,
    loadingClinic,
    reloadingDentists,
    isClinic
  ]);

  const menuItems: MenuItem[] = useMemo(() => {
    const items: MenuItem[] = [
      {
        icon: calendarNumberOutline,
        label: copy.menu.overview,
        onClick: modals.overview.onOpen
      }
    ];

    if (isNativePlatform) {
      items.push({
        icon: swapHorizontalOutline,
        label: copy.menu.switchAccount,
        onClick: onSwitchAccount
      });
    }

    items.push({
      icon: logOutOutline,
      label: copy.menu.signOut,
      onClick: onSignOut
    });

    return items;
  }, [modals.overview.onOpen, onSwitchAccount, onSignOut]);

  const loading: boolean = useMemo(
    () =>
      isClinic === undefined ||
      loadingClinic ||
      !loadedClinic ||
      (isClinic === true && !clinic?.id) ||
      loadingDentists ||
      !loadedDentists ||
      reloadingDentists ||
      isPaused,
    [
      isClinic,
      loadingClinic,
      loadedClinic,
      clinic?.id,
      loadingDentists,
      loadedDentists,
      reloadingDentists,
      isPaused
    ]
  );

  const isProfileLimitReached = useMemo(
    () =>
      dentists.length >= (profileCountLimit ?? PROFILE_COUNT_LIMITS.DEFAULT),
    [profileCountLimit, dentists.length]
  );

  const showAddDentistTile: boolean = useMemo(
    () =>
      !loading &&
      isClinic === true &&
      isNativePlatform &&
      (!isProfileLimitReached || isSubscribed === false),
    [loading, isClinic, isProfileLimitReached, isSubscribed]
  );

  useEffect(() => {
    fetchProfiles();
  }, [isClinic]);

  useEffect(() => {
    const onSaveAccount = async () => {
      const mobileNumber = getUserMobileNumber(user);
      if (loading || !shouldSaveAccount || !mobileNumber) {
        return;
      }

      let displayName: string | null | undefined = undefined;
      if (isClinic && clinic) {
        displayName = clinic.name;
      } else if (!isClinic && dentists.length > 0) {
        const dentist = dentists[0];
        displayName = formatDentistName({
          dentist,
          returnDentistFirstName: true
        });
      }

      if (!displayName) {
        displayName = mobileNumber;
      }
      setTimeout(async () => {
        await saveAccount(displayName);
        await saveMobileNumber(displayName, mobileNumber);
        setShouldSaveAccount(false);
      }, SAVE_ACCOUNT_DELAY_IN_MS);
    };

    onSaveAccount();
  }, [
    loading,
    shouldSaveAccount,
    user?.phone,
    isClinic,
    clinic,
    dentists,
    saveAccount,
    setShouldSaveAccount
  ]);

  useEffect(() => {
    if (isResumed) {
      fetchProfiles();
    }
  }, [isResumed]);

  useEffect(() => {
    if (dentists.length > 0) {
      dentists.forEach((dentist) => {
        queryClient.invalidateQueries({
          queryKey: ['requests', dentist.id]
        });
        queryClient.invalidateQueries({
          queryKey: ['notifications', dentist.id]
        });
      });
    }
  }, [queryClient, dentists]);

  return (
    <IonPage id="select-dentist">
      <IonContent>
        <Padding>
          <Logo
            rightContent={
              <Menu
                triggerIcon={ellipsisVertical}
                triggerClassName="select-dentist__menu-icon"
                items={menuItems}
              />
            }
          />
          <Spacer top={12} bottom={102}>
            {loading ? (
              <Skeleton className="select-dentist__prompt-placeholder" />
            ) : (
              <h1 className="select-dentist__prompt">{copy.prompt}</h1>
            )}
            <Spacer top={24} />
            <div className="select-dentist__dentists">
              {loading
                ? [...Array(3)].map((_, index) => (
                    <DentistTilePlaceholder key={index} />
                  ))
                : dentists.map((dentist) => (
                    <DentistTile
                      key={dentist.id}
                      dentist={dentist}
                      onSelectDentist={() => onSelectDentist(dentist)}
                    />
                  ))}
              {showAddDentistTile && (
                <AddDentistTile
                  onAddDentist={onAddDentist}
                  label={copy.addLabel}
                />
              )}
            </div>
          </Spacer>
          <ButtonContainer>
            <IonButton
              id="sign-out-button"
              onClick={onSignOut}
              expand="block"
              fill="clear"
              size="default"
              color="primary"
              strong={true}
            >
              {copy.signOutButtonText}
            </IonButton>
          </ButtonContainer>
        </Padding>

        <Paywall
          isOpen={showPaywall}
          onClose={onClosePaywall}
          loading={purchasing}
        />

        <IonLoading
          isOpen={loadingAccount}
          message="Loading..."
          spinner="crescent"
        />
      </IonContent>
    </IonPage>
  );
};

export default withSelectDentistModals(SelectDentist);
